import {
  Button,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeaderWithIcon,
  ModalOverlay,
  RadioBox,
  RadioGroup,
} from '@dev-spendesk/grapes';
import React, { useEffect, useState } from 'react';
import { useSessionStorage } from 'react-use';

import { ModalMFAContainer } from 'common/components/ModalMFA';
import { useTranslation } from 'common/hooks/useTranslation';
import { useCompanyId } from 'modules/app/hooks/useCompanyId';
import { useUserId } from 'modules/app/hooks/useUserId';
import { useNotifications } from 'modules/app/notifications';
import { CreateMobileFactor } from 'modules/authentication-factor/components/CreateAuthenticationFactorModal/steps/CreateMobileFactor';
import { CreateTotpFactor } from 'modules/authentication-factor/components/CreateAuthenticationFactorModal/steps/CreateTotp/CreateTotpFactor';
import {
  type AvailableAuthenticationMethods,
  availableAuthenticationMethodTranslations,
} from 'modules/authentication-factor/helpers/getTranslations';
import { useGetUserAuthenticationFactors } from 'modules/authentication-factor/hooks/useGetUserAuthenticationFactors';

const CONFIGURABLE_AUTHENTICATION_METHODS: AvailableAuthenticationMethods[] = [
  'TOTP',
  'MOBILE_APP',
  'SMS',
];

type Steps =
  | 'selectType'
  | 'configureTOTP'
  | 'configureMobileApp'
  | 'configureSMS';

export const SecurityRequirementModal = () => {
  const { t } = useTranslation('global');
  const { successNotif } = useNotifications();
  const companyId = useCompanyId();
  const userId = useUserId();

  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setStep] = useState<Steps>('selectType');
  const [selectedAuthenticationMethod, setSelectedAuthenticationMethod] =
    useState<AvailableAuthenticationMethods | null>(null);
  const [remindMeLater, setRemindMeLater] = useSessionStorage(
    'authenticationMethodSetupReminder',
    false,
  );

  const authenticationFactorsQueryState = useGetUserAuthenticationFactors({
    userId,
    companyId,
  });

  useEffect(() => {
    if (
      authenticationFactorsQueryState.status === 'success' &&
      authenticationFactorsQueryState.data.factors.filter(
        (f) => f.type !== 'EMAIL' && f.type !== 'RECOVERY',
      ).length === 0
    ) {
      setIsOpen(true);
    }
  }, [authenticationFactorsQueryState]);

  const options = CONFIGURABLE_AUTHENTICATION_METHODS.map((type) => {
    const lowercaseType =
      type.toLowerCase() as Lowercase<AvailableAuthenticationMethods>;
    const config = availableAuthenticationMethodTranslations[lowercaseType];

    return {
      value: type,
      label: t(config.label),
      description: t(config.description),
      iconName: config.iconName,
    };
  });

  const handleClose = () => {
    setRemindMeLater(true);
    setStep('selectType');
    setIsOpen(false);
  };

  const handleConfigureSelectedMethod = (
    method: AvailableAuthenticationMethods,
  ) => {
    switch (method) {
      case 'TOTP': {
        setStep('configureTOTP');

        break;
      }
      case 'MOBILE_APP': {
        setStep('configureMobileApp');

        break;
      }
      case 'SMS': {
        setStep('configureSMS');

        break;
      }
      default: {
        // This should never happen if all cases are covered
        throw new Error(`Unhandled authentication method: ${method}`);
      }
    }
  };

  const handleSmsFactorActivated = () => {
    successNotif(t('profile.authenticationFactors.create.sms.successMessage'));
  };

  if (!isOpen || remindMeLater) {
    return null;
  }

  return (
    <ModalOverlay isOpen={isOpen}>
      {currentStep === 'selectType' && (
        <ModalContent
          aria-labelledby="enforce-authentication-method-setup-modal"
          onClose={handleClose}
        >
          <ModalHeaderWithIcon
            title={t('security.newSecurityRequirementModal.title')}
            subtitle={t('security.newSecurityRequirementModal.description')}
            iconName="circle-information"
            iconVariant="info"
            titleId="enforce-authentication-method-setup-modal-title"
          />
          <ModalBody>
            <div className="mt-16">
              <RadioGroup
                name="authenticationFactorType"
                value={selectedAuthenticationMethod}
                direction="column"
                className="gap-8 text-start"
                onChange={(event) =>
                  setSelectedAuthenticationMethod(
                    event.target.value as AvailableAuthenticationMethods,
                  )
                }
              >
                {options.map((option) => (
                  <RadioBox {...option} key={option.value} />
                ))}
              </RadioGroup>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="secondaryNeutral"
              text={t('misc.remindMeLater')}
              onClick={handleClose}
            />
            <Button
              variant="primaryBrand"
              text={t('misc.continue')}
              isDisabled={!selectedAuthenticationMethod}
              onClick={() =>
                handleConfigureSelectedMethod(
                  selectedAuthenticationMethod as AvailableAuthenticationMethods,
                )
              }
            />
          </ModalFooter>
        </ModalContent>
      )}
      {currentStep === 'configureTOTP' && (
        <CreateTotpFactor
          handleClose={handleClose}
          handleBack={() => setStep('selectType')}
        />
      )}
      {currentStep === 'configureMobileApp' && (
        <CreateMobileFactor
          handleClose={handleClose}
          handleBack={() => setStep('selectType')}
        />
      )}
      {currentStep === 'configureSMS' && (
        <ModalMFAContainer
          isOpen={currentStep === 'configureSMS'}
          onFactorActivated={handleSmsFactorActivated}
          onModalClose={() => setStep('selectType')}
          showAppCallout={false}
          verifyPhoneStepTitle={t(
            'profile.authenticationFactors.create.sms.title',
          )}
          verifyPhoneStepSubtitle={t(
            'profile.authenticationFactors.create.sms.description',
          )}
        />
      )}
    </ModalOverlay>
  );
};
